import React, { ChangeEvent, useState } from 'react'
import { useNavigate } from 'react-router'
import { observer } from 'mobx-react-lite'
import {
    Row, Button, ToggleSwitch,
    Tooltip, TooltipTrigger, TooltipContent,
    useUI,
    Icon
} from '@evertel/web/ui'
import { useObjectUrl } from '@evertel/hooks'
import { DocumentIcon } from '../../elements/DocumentIcon'
import { DocIconPickerModal } from './DocIconPickerModal'
import { SchemaCategoriesModal } from './SchemaCategoriesModal'
import { SchemaBuilderController } from '../../../controller'

const DESC_PLACEHOLDER = 'Add a short description...'
interface SettingsBarProps {
    schemaBuilderController: SchemaBuilderController
    /** call before navigating so the unsaved-changes guard does not prompt */
    allowNavigation?: () => void
}

const SettingsBar: React.FC<SettingsBarProps> = observer(({
    schemaBuilderController,
    allowNavigation
}) => {

    const navigate = useNavigate()
    const { addToast, isConfirmed } = useUI()

    // an icon image picked in the modal only uploads on save, so preview it locally until then
    const stagedIconUrl = useObjectUrl(schemaBuilderController.iconFile)

    const [showDocIconPickerModal, setShowDocIconPickerModal] = useState(false)
    const [showCategoriesModal, setShowCategoriesModal] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    // the controller uses `defaultName` as its "unnamed" sentinel (save() checks it);
    // show that as an empty input so the placeholder is visible
    const nameValue = (schemaBuilderController.name === schemaBuilderController.defaultName)
        ? ''
        : schemaBuilderController.name

    const onChangeName = (e: ChangeEvent<HTMLInputElement>) => {
        schemaBuilderController.setName(e.target.value || schemaBuilderController.defaultName)
    }

    const onChangeDescription = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        schemaBuilderController.setDescription(e.target.value)
    }

    const onSave = async () => {
        const isNew = !schemaBuilderController.id

        if (schemaBuilderController.name === schemaBuilderController.defaultName || !schemaBuilderController.name) {
            await isConfirmed({
                title: 'Please name your template',
                message: 'Please name your template before saving',
                acceptButton: {
                    label: 'Ok',
                    color: 'success'
                }
            })
        } else {
            setIsSaving(true)
            try {
                const schema = await schemaBuilderController.save()

                addToast({
                    color: 'success',
                    message: 'Your template has been saved.'
                })

                if (schema?.id && isNew) {
                    allowNavigation?.()
                    navigate(`/everdocs/schema/edit/${schema?.id}`)
                }

            } catch (error: any) {
                addToast({
                    color: 'danger',
                    message: error.message
                })
            }
            setIsSaving(false)
        }
    }

    const categoriesCount = schemaBuilderController.categories.length

    return (
        <Row
            align="between"
            valign="center"
            className="schema-builder-settings-bar">
            <Row valign="center">
                <Tooltip placement="top">
                    <TooltipTrigger asChild>
                        <button
                            onClick={() => setShowDocIconPickerModal(true)}
                            className="btn btn-link me-3"
                        >
                            <DocumentIcon
                                schema={{
                                    icon: schemaBuilderController.icon,
                                    color: schemaBuilderController.color,
                                    iconMedia: { url: stagedIconUrl ?? schemaBuilderController.iconMedia?.url }
                                }}
                                width={48}
                                height={48}
                                className="document-icon-lg"
                            />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent>
                        Click to add an icon or logo to visually identify this template.
                    </TooltipContent>
                </Tooltip>
                <Tooltip placement="top">
                    <TooltipTrigger asChild>
                        <div className="d-flex flex-column align-items-start doc-fields">
                            <input
                                id="name"
                                name="name"
                                type="text"
                                className="doc-name"
                                placeholder={schemaBuilderController.defaultName}
                                aria-label="Template name"
                                value={nameValue}
                                onChange={onChangeName}
                            />
                            <textarea
                                id="description"
                                name="description"
                                className="doc-description"
                                placeholder={DESC_PLACEHOLDER}
                                aria-label="Template description"
                                value={schemaBuilderController.description}
                                onChange={onChangeDescription}
                            />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>
                        Click to edit title and description.
                    </TooltipContent>
                </Tooltip>
            </Row>
            <Row valign="center">
                <Tooltip placement="top">
                    <TooltipTrigger asChild>
                        <span className="me-4">
                            <ToggleSwitch
                                id="isEnabled"
                                label="Enable"
                                checked={schemaBuilderController.isEnabled}
                                onChange={(checked) => schemaBuilderController.setIsEnabled(checked)}
                            />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        Enable the template so that agency members can view it and use it to create EverDocs.
                    </TooltipContent>
                </Tooltip>
                <Tooltip placement="top">
                    <TooltipTrigger asChild>
                        <Button
                            color="dark"
                            className="me-4"
                            onClick={() => setShowCategoriesModal(true)}>
                            <Icon name="folder-open" className="me-2" />
                            Categories {categoriesCount ? `(${categoriesCount})` : ''}
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        Assign to one or more categories.
                    </TooltipContent>
                </Tooltip>
                <Button
                    color="primary"
                    className="fw-bold"
                    loading={isSaving}
                    onClick={onSave}>
                    Save
                </Button>
            </Row>

            <DocIconPickerModal
                visible={showDocIconPickerModal}
                onClose={() => setShowDocIconPickerModal(false)}
                schemaBuilderController={schemaBuilderController}
            />
            <SchemaCategoriesModal
                visible={showCategoriesModal}
                onClose={() => setShowCategoriesModal(false)}
                schemaBuilderController={schemaBuilderController}
            />
        </Row>
    )
})

export {
    SettingsBar
}
